import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

// Lightweight interface (shared shape with property/room images)
interface RoomGalleryImage {
  id?: number;
  imageUrl?: string;
  fileName?: string;
  isMainImage?: boolean;
}

interface RoomGalleryProps {
  images?: (string | RoomGalleryImage)[];
  height?: number;
}

const normalize = (images?: (string | RoomGalleryImage)[]): string[] => {
  if (!images || images.length === 0) return [];
  return images
    .map(img => {
      if (typeof img === 'string') return img;
      if (!img) return '';
      // Prefer explicit imageUrl, else derive from fileName
      if (img.imageUrl) return img.imageUrl;
      if (img.fileName) {
        // If fileName already looks like a URL use as-is, else assume it's in /Images
        return /^(https?:)?\/\//i.test(img.fileName) ? img.fileName : `/Images/${img.fileName}`;
      }
      return '';
    })
    .filter(Boolean);
};

const RoomGallery: React.FC<RoomGalleryProps> = ({ images, height = 280 }) => {
  const normalized = normalize(images);
  const initialMain = normalized.find((_, idx) => {
    if (!images) return idx === 0;
    const obj = images[idx] as any;
    return obj && typeof obj === 'object' && obj.isMainImage;
  }) || normalized[0];

  const [mainImage, setMainImage] = useState<string>(initialMain);

  useEffect(() => {
    const n = normalize(images);
    const main = n.find((_, idx) => {
      if (!images) return idx === 0;
      const obj = images[idx] as any;
      return obj && typeof obj === 'object' && obj.isMainImage;
    }) || n[0];
    setMainImage(main);
  }, [images]);

  return (
    <div className="row gx-3 w-100 mx-0">
      <div className="col-12 col-sm-8 mb-3 mb-sm-0">
        <img
          src={mainImage}
          className="img-fluid rounded-3 w-100"
          style={{ height: '100%', maxHeight: height, objectFit: 'cover' }}
          alt="Room main"
        />
      </div>
      <div className="col-12 col-sm-4 position-relative">
        <Swiper
          modules={[Navigation]}
          direction="vertical"
          spaceBetween={10}
          slidesPerView={2} // fixed client-approved design
          navigation={{
            nextEl: '.swiper-button-next-vertical',
            prevEl: '.swiper-button-prev-vertical'
          }}
          style={{ height }}
        >
          {normalized.map((img, idx) => (
            <SwiperSlide key={idx}>
              <img
                src={img}
                className={`img-fluid rounded-2 w-100 ${img === mainImage ? 'border border-2 border-dark' : ''}`}
                style={{
                  height: (height - 20) / 2,
                  objectFit: 'cover',
                  cursor: 'pointer'
                }}
                onClick={() => setMainImage(img)}
                alt={`Room thumb ${idx}`}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <div
          className="swiper-button-prev-vertical position-absolute top-0 start-50 translate-middle-x d-flex justify-content-center"
          style={{ zIndex: 5 }}
          aria-label="Previous images"
        >
          <button
            type="button"
            className="btn btn-light btn-sm rounded-circle shadow-sm"
            style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <i className="pi pi-chevron-up" />
          </button>
        </div>
        <div
          className="swiper-button-next-vertical position-absolute bottom-0 start-50 translate-middle-x d-flex justify-content-center"
          style={{ zIndex: 5 }}
          aria-label="Next images"
        >
          <button
            type="button"
            className="btn btn-light btn-sm rounded-circle shadow-sm"
            style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <i className="pi pi-chevron-down" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomGallery;
